import { ComponentPropsWithoutRef, MouseEvent, forwardRef } from "react";
import cx from "classnames";
import styles from "./Announcement.module.scss";
import { Button } from "../Button";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Info from "@servicetitan/hammer-icon/mdi/round/info.svg";
import Warning from "@servicetitan/hammer-icon/mdi/round/warning.svg";
import Danger from "@servicetitan/hammer-icon/mdi/round/error.svg";
import Success from "@servicetitan/hammer-icon/mdi/round/check_circle.svg";
import { Icon } from "../Icon";
import { OnCloseProps, LayoutUtilProps, Status, Svg } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Announcement component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 * @extends OnCloseProps
 */
export type AnnouncementProps = ComponentPropsWithoutRef<"div"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> &
  OnCloseProps & {
    /**
     * The visual style of the announcement.
     * @default info
     */
    status?: Status;
    /**
     * The title text displayed in the announcement.
     */
    title: string;
  };

const statusIconMap: Record<NonNullable<AnnouncementProps["status"]>, Svg> = {
  info: Info,
  success: Success,
  warning: Warning,
  danger: Danger,
};

/**
 * Announcement component for displaying important messages to users.
 *
 * Features:
 * - Supports different visual styles (info, success, warning, danger)
 * - Optional close button for dismissible announcements
 * - Customizable title and content
 * - Accessible with proper ARIA roles
 * - Includes status-specific icons
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <Announcement
 *   title="New Feature"
 *   onClose={() => console.log('Announcement closed')}
 * >
 *   Check out our latest feature!
 * </Announcement>
 *
 * @example
 * <Announcement
 *   status="warning"
 *   title="Maintenance Notice"
 * >
 *   Scheduled maintenance will begin in 30 minutes.
 * </Announcement>
 */
export const Announcement = forwardRef<HTMLDivElement, AnnouncementProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      title,
      status = "info",
      children,
      style,
      onClose,
      ...rest
    } = componentProps;

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const AnnouncementCx = cx(styles["announcement"], className, {
      [styles["success"]]: status === "success",
      [styles["warning"]]: status === "warning",
      [styles["danger"]]: status === "danger",
    });
    return (
      <div
        role="status"
        className={AnnouncementCx}
        data-anv="announcement"
        style={styleCombined}
        {...rest}
        ref={ref}
      >
        <span className={styles["title-container"]}>
          <Icon
            size="large"
            className={styles["status-icon"]}
            svg={statusIconMap[status]}
          />

          <span className={styles["title"]}>{title}</span>
        </span>

        {children}

        {onClose ? (
          <span className={styles["close-container"]}>
            <Button
              icon={Close}
              size="small"
              appearance="ghost"
              className={styles["close-button"]}
              onClick={(e: MouseEvent<HTMLButtonElement>) => onClose(e)}
              aria-label={`Dismiss ${status} announcement`}
            />
          </span>
        ) : null}
      </div>
    );
  },
);

Announcement.displayName = "Announcement";
